import { db } from '@/firebase-config';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';

import { callBackendCommand, firebaseFunctionCommands, getCurrentIdentitySession } from '@/backend';
import { getEffectiveUserId } from '@/features/auth/current-user-identity';
import { logger } from '@/utils/logger';
import { buildProjectSubcollectionPath, type ProjectScope } from './project-scope';

export type NoteRecoverySnapshotSource =
    | 'legacy-indexeddb-capture'
    | 'manual-admin'
    | 'pre-restore'
    | 'firestore-backup-import';

export interface SubmitNoteRecoverySnapshotRequest {
    orgId: string;
    projectId: string;
    noteId: string;
    base64Update: string;
    source: NoteRecoverySnapshotSource;
    reason: string;
    title?: string;
}

export interface SubmitNoteRecoverySnapshotResponse {
    success: true;
    snapshotId: string;
    path: string;
    sizeBytes: number;
}

/**
 * Saves a backup of the Yjs document state to Firestore.
 */
export async function saveNoteBackup(
    scope: ProjectScope,
    noteId: string,
    base64Blob: string,
    metadata: Record<string, unknown> = {},
): Promise<void> {
    try {
        if (!getCurrentIdentitySession()) return;
        const userId = getEffectiveUserId();
        if (!userId) return;

        const noteRef = doc(db, buildProjectSubcollectionPath(scope, 'notes'), noteId);
        const backupMetadata = typeof metadata.title === 'string'
            ? { backupTitle: metadata.title }
            : {};

        await setDoc(noteRef, {
            backupBlob: base64Blob,
            backupUpdatedAt: serverTimestamp(),
            backupLastModifiedBy: userId,
            ...backupMetadata,
        }, { merge: true });

        logger.cloud(`Saved backup for note ${noteId}`);
    } catch (error: unknown) {
        logger.error('Error saving note backup:', error);
    }
}

export async function submitNoteRecoverySnapshot(
    request: SubmitNoteRecoverySnapshotRequest,
): Promise<SubmitNoteRecoverySnapshotResponse | null> {
    if (!getCurrentIdentitySession()) return null;

    return callBackendCommand(firebaseFunctionCommands.saveNoteRecoverySnapshot, request);
}
